import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, federalIncomeTax } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";
import { STATE_TAX_LIST } from "@/data/stateTax";

export default function IncomeTaxPage() {
    const [gross, setGross] = useState(85000);
    const [filing, setFiling] = useState("single");

    const result = useMemo(() => {
        const { tax, taxable, deduction } = federalIncomeTax(toNum(gross), filing);
        const takeHome = toNum(gross) - tax;
        const effective = toNum(gross) > 0 ? (tax / toNum(gross)) * 100 : 0;
        return { tax, taxable, deduction, takeHome, effective };
    }, [gross, filing]);

    return (
        <CalculatorShell
            slug="income-tax"
            explainer={
                                <>
                    <h2>How this income tax calculator works</h2>
                    <p>
                        An income tax calculator estimates your federal income tax liability based on filing
                        status, taxable income, deductions, and credits. Use it to plan contributions, model
                        year-end moves, or simply understand what you owe before filing.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Progressive brackets:</strong> only the income within each bracket is taxed at that bracket's rate — crossing into a higher bracket does not tax all income at the higher rate.</li>
                        <li><strong>Standard deduction:</strong> reduces taxable income before brackets are applied — most filers use this instead of itemizing.</li>
                        <li><strong>Effective rate:</strong> total tax ÷ gross income — your actual average tax burden, lower than your marginal rate.</li>
                        <li><strong>Marginal rate:</strong> the rate applied to your last dollar of income — the relevant rate for planning decisions like Roth vs. Traditional contributions.</li>
                        <li><strong>Federal only:</strong> state income taxes vary widely and are not included in this estimate.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Maximize pre-tax retirement contributions (401(k), Traditional IRA, HSA) to reduce taxable income before calculating your bracket.</li>
                        <li>Your marginal rate — not your effective rate — is what matters when deciding whether to defer income or accelerate deductions.</li>
                        <li>Don't confuse gross income with taxable income; after deductions, most people's taxable income is thousands of dollars lower.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Gross income" prefix="$" value={gross} onChange={setGross} dataTestId="calc-input-gross" />
                    <div>
                        <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-2">
                            Filing status
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { v: "single", l: "Single" },
                                { v: "married", l: "Married, joint" },
                            ].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setFiling(o.v)}
                                    data-testid={`filing-${o.v}`}
                                    className={`h-12 rounded-md border text-sm transition-colors ${
                                        filing === o.v ? "bg-primary text-primary-foreground border-primary" : "border-border text-primary hover:border-primary/40"
                                    }`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Take-home (federal only)"
                        value={result.takeHome}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "Federal tax", value: result.tax, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-tax" },
                            { label: "Effective rate", value: result.effective, format: (n) => fmtPct(n, 1), testId: "calc-result-effective" },
                            { label: "Taxable income", value: result.taxable, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-taxable" },
                            { label: "Std deduction", value: result.deduction, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-deduction" },
                        ]}
                    />
                </div>
            </div>

            {/* Browse by state — internal-link juice for 50 programmatic SEO pages */}
            <div className="mt-10 bg-card border border-border rounded-xl p-6 sm:p-8">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">State income tax</div>
                <h3 className="font-serif text-2xl sm:text-3xl text-primary mt-1 mb-2">Calculate your state's income tax</h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
                    Federal tax is only half the story. Pick your state for a calculator that layers state income tax + FICA on top of the federal math — with editorial commentary on local quirks (Prop 13, Texas property-tax tradeoff, Massachusetts millionaire's tax, etc.).
                </p>
                <div className="flex flex-wrap gap-2" data-testid="income-tax-state-browse">
                    {STATE_TAX_LIST.map((s) => (
                        <Link
                            key={s.code}
                            to={`/calculator/income-tax/${s.code}`}
                            data-testid={`income-tax-state-${s.code}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                s.noTax
                                    ? "border-accent/40 bg-accent/5 text-accent hover:border-accent"
                                    : "border-border text-primary hover:border-primary/40"
                            }`}
                        >
                            {s.name}
                            {s.noTax && <span className="ml-1 opacity-70">· no tax</span>}
                        </Link>
                    ))}
                </div>
            </div>
        </CalculatorShell>
    );
}
